import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, fetchConfig } from '@theme/utilities';

/**
 * A custom element that adds or removes the free gift wrap (one per order)
 * and saves the gift message as a cart attribute.
 */
class CartGiftWrap extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#removeOrphanGiftWrap);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#removeOrphanGiftWrap);
  }

  get #variantId() {
    return Number(this.dataset.variantId);
  }

  /**
   * Posts to /cart/update.js, aborting any request still in flight.
   * @param {Object} body
   */
  async #updateCart(body) {
    if (this.#activeFetch) this.#activeFetch.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: abortController.signal,
      });
      return await response.json();
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /**
   * Adds or removes the gift wrap line when the checkbox changes.
   * @param {Event} event
   */
  toggleGiftWrap = async (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const checked = event.target.checked;
    const message = this.querySelector('textarea')?.value ?? '';

    this.setAttribute('aria-busy', 'true');

    try {
      const cart = await this.#updateCart({
        updates: { [this.#variantId]: checked ? 1 : 0 },
        attributes: {
          'Gift wrap': checked ? 'Yes' : '',
          'Gift message': checked ? message : '',
        },
      });

      // No sections in the payload: every cart-items-component re-renders itself.
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id, { itemCount: cart.item_count, source: 'cart-gift-wrap' })
      );
    } catch (error) {
      event.target.checked = !checked;
    } finally {
      this.removeAttribute('aria-busy');
    }
  };

  /**
   * Saves the gift message as the user types.
   * @param {InputEvent} event
   */
  updateGiftMessage = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    try {
      await this.#updateCart({ attributes: { 'Gift message': event.target.value } });
    } catch (error) {}
  }, 300);

  /**
   * Keeps the cart consistent after other cart changes: removes the gift wrap when it is
   * the only thing left, and clears the gift attributes when the wrap line was deleted.
   * @param {Event} event
   */
  #removeOrphanGiftWrap = async (event) => {
    if (!(event instanceof CartUpdateEvent) || event.detail.data?.source === 'cart-gift-wrap') return;

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    const hasGiftWrap = cart.items.some((item) => item.variant_id === this.#variantId);
    const onlyGiftWrap = hasGiftWrap && cart.items.every((item) => item.variant_id === this.#variantId);
    const staleAttributes = !hasGiftWrap && (cart.attributes['Gift wrap'] || cart.attributes['Gift message']);

    if (staleAttributes) {
      await this.#updateCart({ attributes: { 'Gift wrap': '', 'Gift message': '' } });
      return;
    }

    if (!onlyGiftWrap) return;

    const updatedCart = await this.#updateCart({
      updates: { [this.#variantId]: 0 },
      attributes: { 'Gift wrap': '', 'Gift message': '' },
    });

    this.dispatchEvent(
      new CartUpdateEvent(updatedCart, this.id, { itemCount: updatedCart.item_count, source: 'cart-gift-wrap' })
    );
  };
}

if (!customElements.get('cart-gift-wrap')) {
  customElements.define('cart-gift-wrap', CartGiftWrap);
}
